import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/lib/supabase";
import { gcsUploadFile, gcsDeleteFile, gcsGetSignedReadUrl } from "@/lib/gcsStorage";
import { parseAiFlowResponse } from "@/lib/parseAiFlowResponse";
import { useGcsUpload } from "@/hooks/useGcsUpload";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import QuoteModalV2 from "@/components/modals/QuoteModalV2";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Upload,
  Eye,
  FileText,
  ChevronUp,
  ChevronDown,
  Minus,
  AlertCircle,
  MapPin,
  Building,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  MoreVertical,
  Trash2,
  UserX,
  Edit,
  History
} from "lucide-react";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Badge } from "@/components/ui/badge";

const statusConfig = {
  completed: {
    label: "Valid",
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
    icon: CheckCircle,
    iconColor: "text-emerald-600"
  },
  compliant: {
    label: "Valid",
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
    icon: CheckCircle,
    iconColor: "text-emerald-600"
  },
  pending: {
    label: "Missing",
    color: "bg-orange-50 text-orange-700 border-orange-200",
    icon: AlertCircle,
    iconColor: "text-orange-600"
  },
  in_progress: {
    label: "Booked",
    color: "bg-blue-50 text-blue-700 border-blue-200",
    icon: Clock,
    iconColor: "text-blue-600"
  },
  booked: {
    label: "Booked",
    color: "bg-blue-50 text-blue-700 border-blue-200",
    icon: Clock,
    iconColor: "text-blue-600"
  },
  quoting: {
    label: "Quoting",
    color: "bg-purple-50 text-purple-700 border-purple-200",
    icon: Clock,
    iconColor: "text-purple-600"
  },
  expiring_soon: {
    label: "Expiring",
    color: "bg-yellow-50 text-yellow-700 border-yellow-200",
    icon: AlertCircle,
    iconColor: "text-yellow-600"
  },
  expired: {
    label: "Expired",
    color: "bg-red-50 text-red-700 border-red-200",
    icon: XCircle,
    iconColor: "text-red-600"
  },
  awaiting_certification: {
    label: "Missing",
    color: "bg-orange-50 text-orange-700 border-orange-200",
    icon: AlertCircle,
    iconColor: "text-orange-600"
  }
};

const getQualificationStatus = (assignment) => {
  if (!assignment) return 'pending';

  const now = new Date();
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  // Check if expired
  if (assignment.expiry_date && new Date(assignment.expiry_date) <= now) {
    return 'expired';
  }

  // Check if expiring soon (within 30 days)
  if (assignment.status === 'completed' && assignment.expiry_date) {
    const expiryDate = new Date(assignment.expiry_date);
    if (expiryDate > now && expiryDate <= thirtyDaysFromNow) {
      return 'expiring_soon';
    }
    return 'compliant';
  }

  // Return the assignment status
  return assignment.status || 'pending';
};

export function QualificationsTable({ data = null, onUploadSuccess, onManualUploadConfirm }) {
  const { userProfile } = useAuth();
  const { getViewableUrl } = useGcsUpload();
  const [sortField, setSortField] = useState("requirement_name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [hoveredQualificationId, setHoveredQualificationId] = useState(null);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingQualification, setEditingQualification] = useState(null);
  const [editFormData, setEditFormData] = useState({ expiryDate: '', certificateNumber: '' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [actionQualification, setActionQualification] = useState(null);
  const [certificateHistoryOpen, setCertificateHistoryOpen] = useState(false);
  const [certificateHistory, setCertificateHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Transform real data to match the table format
  const transformedQualifications = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Map all qualifications
    const allQuals = data.map(assignment => {
      // Handle certificate URL - convert storage path to full URL if needed
      let certificateUrl = assignment.evidence_url;
      if (certificateUrl && !certificateUrl.startsWith('http')) {
        // It's a storage path in the private 'certificates' bucket - need signed URL
        // Note: We'll generate signed URLs on demand when viewing
        certificateUrl = certificateUrl; // Keep the path as-is for now
      }

      return {
        ...assignment,
        name: assignment.requirement_name || 'Unknown Requirement',
        category: assignment.requirement_category || 'General',
        status: getQualificationStatus(assignment),
        issued: assignment.completed_date || null,
        expiry: assignment.expiry_date,
        certificateUrl: certificateUrl,
        // Use spaces array if provided, otherwise fall back to old project_name format
        site: assignment.spaces && assignment.spaces.length > 0 ? assignment.spaces[0] :
              (assignment.project_name ? {
                name: assignment.project_name,
                location: assignment.project_location,
                id: assignment.project_id
              } : null),
        // Keep the full spaces array for display
        spaces: assignment.spaces || []
      };
    });

    // Group by requirement name - keep best status as primary, track secondary statuses
    const groupedByName = new Map();

    console.log('QualificationsTable - all qualifications before grouping:', allQuals);

    // Status priority: completed > expiring > expired > booked > quoting > pending
    const statusPriority = {
      'completed': 1,
      'compliant': 1,
      'expiring_soon': 2,
      'expired': 3,
      'booked': 4,
      'in_progress': 4,
      'quoting': 5,
      'pending': 6,
      'awaiting_certification': 6
    };

    // Helper to merge sites without duplicates
    const mergeSites = (existingSites, newSpaces, newSite) => {
      const merged = [...existingSites];
      const newSites = newSpaces && newSpaces.length > 0 ? newSpaces : (newSite ? [newSite] : []);

      newSites.forEach(site => {
        // Check if site already exists (by id or name)
        const exists = merged.some(s =>
          (s.id && site.id && s.id === site.id) ||
          (s.name && site.name && s.name === site.name)
        );
        if (!exists) {
          merged.push(site);
        }
      });

      return merged;
    };

    allQuals.forEach(qual => {
      const existing = groupedByName.get(qual.name);

      if (!existing) {
        // First occurrence
        groupedByName.set(qual.name, {
          ...qual,
          sites: qual.spaces && qual.spaces.length > 0 ? qual.spaces : (qual.site ? [qual.site] : []),
          secondaryStatuses: [] // Track other statuses
        });
      } else {
        const currentPriority = statusPriority[qual.status] || 99;
        const existingPriority = statusPriority[existing.status] || 99;

        // Always merge sites from the new qualification into the existing one
        const mergedSites = mergeSites(existing.sites, qual.spaces, qual.site);

        // If current has better priority, make it primary and add existing to secondary
        if (currentPriority < existingPriority) {
          console.log(`Replacing ${qual.name}: ${qual.status} (priority ${currentPriority}) > ${existing.status} (priority ${existingPriority})`);

          // Add the old primary status to secondary statuses
          existing.secondaryStatuses.push({
            status: existing.status,
            id: existing.id,
            created_date: existing.created_date
          });

          groupedByName.set(qual.name, {
            ...qual,
            sites: mergedSites,
            secondaryStatuses: existing.secondaryStatuses
          });
        } else {
          // Keep existing as primary, just update sites and add current as secondary if different status
          existing.sites = mergedSites;

          if (qual.status !== existing.status) {
            console.log(`Adding ${qual.name} status ${qual.status} as secondary to primary ${existing.status}`);
            existing.secondaryStatuses.push({
              status: qual.status,
              id: qual.id,
              created_date: qual.created_date
            });
          }
        }
      }
    });

    const result = Array.from(groupedByName.values());
    console.log('QualificationsTable - after grouping by name with secondary statuses:', result);
    return result;
  }, [data]);

  // Filter qualifications based on search query
  const filteredQualifications = useMemo(() => {
    if (!searchQuery.trim()) return transformedQualifications;

    const query = searchQuery.toLowerCase();
    return transformedQualifications.filter(qual => {
      // Search in qualification name
      if (qual.name?.toLowerCase().includes(query)) return true;

      // Search in category
      if (qual.category?.toLowerCase().includes(query)) return true;

      // Search in status
      const statusInfo = statusConfig[qual.status];
      if (statusInfo?.label?.toLowerCase().includes(query)) return true;

      // Search in space names
      if (qual.sites?.some(site => site.name?.toLowerCase().includes(query))) return true;

      return false;
    });
  }, [transformedQualifications, searchQuery]);

  const sortedQualifications = useMemo(() => {
    return [...filteredQualifications].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested site sorting
      if (sortField === 'site') {
        aValue = a.site?.name || '';
        bValue = b.site?.name || '';
      }

      // Handle status sorting by priority
      if (sortField === 'status') {
        const statusPriority = {
          'expired': 1,
          'expiring_soon': 2,
          'pending': 3,
          'awaiting_certification': 4,
          'booked': 5,
          'in_progress': 6,
          'quoting': 7,
          'completed': 8,
          'compliant': 9
        };
        aValue = statusPriority[a.status] || 99;
        bValue = statusPriority[b.status] || 99;
      }

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [filteredQualifications, sortField, sortDirection]);

  const handleRequestQuote = async (qualification) => {
    // Prepare the cell data structure expected by QuoteModalV2
    // If this is from a profile page (has user_id), we need to get/create a team_member_id
    let memberId = qualification.team_member_id;

    if (!memberId && qualification.user_id) {
      // This is from profile page - need to get team_member_id
      // Get the first project this requirement is assigned to
      const { data: teamMember } = await supabase
        .from('team_members')
        .select('id')
        .eq('user_id', qualification.user_id)
        .limit(1)
        .single();

      if (teamMember) {
        memberId = teamMember.id;
      } else {
        toast.error('Could not find team member record');
        return;
      }
    }

    const cellData = {
      member: {
        id: memberId,
        name: userProfile?.first_name && userProfile?.last_name
          ? `${userProfile.first_name} ${userProfile.last_name}`.trim()
          : userProfile?.email || 'User',
        email: userProfile?.email
      },
      requirement: {
        id: qualification.requirement_id,
        name: qualification.name,
        category: qualification.category
      }
    };

    setSelectedCell(cellData);
    setQuoteModalOpen(true);
  };

  const handleViewCertificate = async (certificateUrl) => {
    try {
      const url = await getViewableUrl(certificateUrl, 'certificates');
      if (url) window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing certificate:', error);
      toast.error('Failed to open certificate');
    }
  };

  const handleFileUpload = async (qualificationId) => {
    // Create a file input and trigger it
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.jpg,.jpeg,.png';

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const loadingToast = toast.loading('AI is analyzing certificate...');

      try {
        // Find the qualification details
        const qualification = transformedQualifications.find(q => q.id === qualificationId);
        if (!qualification) {
          toast.error('Qualification not found', { id: loadingToast });
          return;
        }

        // Upload to temp location in GCS
        const tempFileName = `temp/${Date.now()}_${file.name}`;
        let uploadResult;
        try {
          uploadResult = await gcsUploadFile(file, {
            folderPath: 'certificates/temp',
            fileName: `${Date.now()}_${file.name}`,
            contentType: file.type,
          });
        } catch (uploadError) {
          throw uploadError;
        }

        const tempFilePath = uploadResult.filePath;

        // Get signed URL for doc parser
        const signedUrl = await gcsGetSignedReadUrl(tempFilePath);

        // Call doc parser webhook
        const response = await fetch('https://addai-flows-backend-29522465016.europe-west2.run.app/webhook/e2a909a7-6f57-461d-83b8-b1506cf3a79a/process-certificate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            documentUrl: signedUrl,
            fileName: file.name,
            uploadedAt: new Date().toISOString()
          })
        });

        if (!response.ok) {
          toast.error('Failed to parse certificate', { id: loadingToast });
          await gcsDeleteFile(tempFilePath);
          return;
        }

        const responseData = await response.json();
        const parsed = parseAiFlowResponse(responseData);

        if (!parsed.success || !parsed.extractedData) {
          toast.error('Could not extract information from certificate', { id: loadingToast });
          await gcsDeleteFile(tempFilePath);
          return;
        }

        const extractedData = parsed.extractedData;

        // Extract data
        const firstNames = Array.isArray(extractedData.first_name)
          ? extractedData.first_name
          : [extractedData.first_name];
        const lastNames = Array.isArray(extractedData.last_name)
          ? extractedData.last_name
          : [extractedData.last_name];
        const courseTitles = Array.isArray(extractedData.course_title)
          ? extractedData.course_title
          : [extractedData.course_title];
        const expiryDate = Array.isArray(extractedData.expiry_date)
          ? extractedData.expiry_date[0]
          : extractedData.expiry_date;
        const certNumber = Array.isArray(extractedData.certificate_number)
          ? extractedData.certificate_number[0]
          : extractedData.certificate_number;

        console.log('Extracted data:', { firstNames, lastNames, courseTitles, expiryDate, certNumber });

        // Generate warnings
        const warnings = [];

        // Check if name matches (if we have team_member_name or can get user name)
        const userName = qualification.team_member_name || qualification.user_name || '';
        if (userName && firstNames.length && lastNames.length) {
          let nameMatches = false;
          for (const firstName of firstNames) {
            for (const lastName of lastNames) {
              const fullName = `${firstName} ${lastName}`.toLowerCase();
              const reverseFullName = `${lastName} ${firstName}`.toLowerCase();
              const userNameLower = userName.toLowerCase();

              if (userNameLower.includes(fullName) || userNameLower.includes(reverseFullName) ||
                  fullName.includes(userNameLower) || reverseFullName.includes(userNameLower)) {
                nameMatches = true;
                break;
              }
            }
            if (nameMatches) break;
          }

          if (!nameMatches) {
            warnings.push({
              type: 'name_mismatch',
              message: `Certificate name (${firstNames[0]} ${lastNames[0]}) doesn't match user (${userName})`
            });
          }
        }

        // Check if course title matches qualification name
        const qualName = qualification.requirement_name || '';
        if (qualName && courseTitles.length) {
          let courseMatches = false;

          const normalizeText = (text) => text
            .toLowerCase()
            .replace(/\b(training|course|certification|certificate)\b/gi, '')
            .replace(/\s+/g, ' ')
            .trim();

          const normalizedQual = normalizeText(qualName);

          for (const courseTitle of courseTitles) {
            const normalizedCourse = normalizeText(courseTitle);

            if (qualName.toLowerCase().includes(courseTitle.toLowerCase()) ||
                courseTitle.toLowerCase().includes(qualName.toLowerCase()) ||
                normalizedQual.includes(normalizedCourse) ||
                normalizedCourse.includes(normalizedQual)) {
              courseMatches = true;
              break;
            }
          }

          if (!courseMatches) {
            warnings.push({
              type: 'course_mismatch',
              message: `Certificate course (${courseTitles[0]}) doesn't match qualification (${qualName})`
            });
          }
        }

        toast.success('Certificate analyzed', { id: loadingToast });

        // Show confirmation with warnings
        if (onManualUploadConfirm) {
          onManualUploadConfirm({
            qualification,
            file,
            tempFilePath: tempFilePath,
            extractedData: {
              expiryDate,
              certificateNumber: certNumber,
              firstName: firstNames[0],
              lastName: lastNames[0],
              courseTitle: courseTitles[0]
            },
            warnings
          });
        }

      } catch (error) {
        console.error('Error uploading certificate:', error);
        toast.error('Failed to upload certificate', { id: loadingToast });
      }
    };

    input.click();
  };

  // Open edit modal
  const handleEditCertificate = (qualification) => {
    setEditingQualification(qualification);
    setEditFormData({
      expiryDate: qualification.expiry || '',
      certificateNumber: qualification.certificate_number || ''
    });
    setEditModalOpen(true);
  };

  // Save edited certificate data
  const handleSaveEdit = async () => {
    if (!editingQualification) return;

    const savingToast = toast.loading('Updating certificate...');

    try {
      const updateData = {
        expiry_date: editFormData.expiryDate || null,
        certificate_number: editFormData.certificateNumber || null,
        updated_date: new Date().toISOString()
      };

      // ALWAYS update user_qualifications (single source of truth)
      if (!editingQualification.user_id) {
        throw new Error('Missing user_id - cannot update qualification');
      }

      const { error: updateError } = await supabase
        .from('user_qualifications')
        .update(updateData)
        .eq('user_id', editingQualification.user_id)
        .eq('requirement_id', editingQualification.requirement_id);

      if (updateError) throw updateError;

      toast.success('Certificate updated successfully', { id: savingToast });
      setEditModalOpen(false);
      setEditingQualification(null);

      // Trigger refresh
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      console.error('Error updating certificate:', error);
      toast.error('Failed to update certificate', { id: savingToast });
    }
  };

  // Open delete confirmation
  const handleDeleteCertificateClick = (qualification) => {
    setActionQualification(qualification);
    setDeleteConfirmOpen(true);
  };

  // Handle deleting certificate only (keeps assignment)
  const handleDeleteCertificate = async () => {
    if (!actionQualification) return;

    const deletingToast = toast.loading('Deleting certificate...');

    try {
      // Delete file from storage if it exists
      if (actionQualification.certificateUrl) {
        try {
          await gcsDeleteFile(actionQualification.certificateUrl);
        } catch (storageError) {
          console.error('Error deleting file from storage:', storageError);
        }
      }

      // Update the record to remove certificate reference
      if (actionQualification.user_id) {
        // Profile page: Update user_qualifications
        const { error: updateError } = await supabase
          .from('user_qualifications')
          .update({
            evidence_url: null,
            certificate_number: null,
            status: 'pending',
            completed_date: null,
            updated_date: new Date().toISOString()
          })
          .eq('user_id', actionQualification.user_id)
          .eq('requirement_id', actionQualification.requirement_id);

        if (updateError) throw updateError;
      } else {
        // Matrix page: Update compliance_assignments
        const { error: updateError } = await supabase
          .from('compliance_assignments')
          .update({
            evidence_url: null,
            certificate_number: null,
            status: 'pending',
            completed_date: null
          })
          .eq('id', actionQualification.id);

        if (updateError) throw updateError;
      }

      toast.success('Certificate deleted successfully', { id: deletingToast });
      setDeleteConfirmOpen(false);
      setActionQualification(null);

      // Trigger refresh
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      console.error('Error deleting certificate:', error);
      toast.error('Failed to delete certificate', { id: deletingToast });
    }
  };

  // Open remove confirmation
  const handleRemoveAssignmentClick = (qualification) => {
    setActionQualification(qualification);
    setRemoveConfirmOpen(true);
  };

  // Handle removing the entire qualification assignment
  const handleRemoveAssignment = async () => {
    if (!actionQualification) return;

    const removingToast = toast.loading('Removing assignment...');

    try {
      // Delete file from storage if it exists
      if (actionQualification.certificateUrl) {
        try {
          await gcsDeleteFile(actionQualification.certificateUrl);
        } catch (storageError) {
          console.error('Error deleting file from storage:', storageError);
        }
      }

      // Delete the assignment record
      if (actionQualification.user_id) {
        // Profile page: Delete from user_qualifications
        const { error: deleteError } = await supabase
          .from('user_qualifications')
          .delete()
          .eq('user_id', actionQualification.user_id)
          .eq('requirement_id', actionQualification.requirement_id);

        if (deleteError) throw deleteError;
      } else {
        // Matrix page: Delete from compliance_assignments
        const { error: deleteError } = await supabase
          .from('compliance_assignments')
          .delete()
          .eq('id', actionQualification.id);

        if (deleteError) throw deleteError;
      }

      toast.success('Assignment removed successfully', { id: removingToast });
      setRemoveConfirmOpen(false);
      setActionQualification(null);

      // Trigger refresh
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      console.error('Error removing assignment:', error);
      toast.error('Failed to remove assignment', { id: removingToast });
    }
  };

  // Load certificate history for a qualification
  const handleViewCertificateHistory = async (qualification) => {
    setActionQualification(qualification);
    setCertificateHistoryOpen(true);
    setLoadingHistory(true);

    try {
      // Get all records for this user and requirement NAME (not just requirement_id)
      // This ensures we see all certificates across different spaces for the same qualification
      if (qualification.user_id) {
        // First get all requirement_ids for this qualification name
        const { data: requirements, error: reqError } = await supabase
          .from('compliance_requirements')
          .select('id')
          .eq('name', qualification.requirement_name);

        if (reqError) throw reqError;

        const requirementIds = requirements?.map(r => r.id) || [];

        if (requirementIds.length === 0) {
          setCertificateHistory([]);
          setLoadingHistory(false);
          return;
        }

        // Get all user_qualifications for these requirement_ids
        const { data, error } = await supabase
          .from('user_qualifications')
          .select('*')
          .eq('user_id', qualification.user_id)
          .in('requirement_id', requirementIds)
          .order('updated_date', { ascending: false });

        if (error) throw error;

        setCertificateHistory(data || []);
      } else {
        // From matrix page - get from compliance_assignments
        // Get team_member's user_id first
        const { data: teamMember } = await supabase
          .from('team_members')
          .select('user_id')
          .eq('id', qualification.team_member_id)
          .single();

        if (teamMember?.user_id) {
          // Get all requirement_ids for this qualification name
          const { data: requirements, error: reqError } = await supabase
            .from('compliance_requirements')
            .select('id')
            .eq('name', qualification.name);

          if (reqError) throw reqError;

          const requirementIds = requirements?.map(r => r.id) || [];

          if (requirementIds.length === 0) {
            setCertificateHistory([]);
            setLoadingHistory(false);
            return;
          }

          // Get all user_qualifications for these requirement_ids
          const { data, error } = await supabase
            .from('user_qualifications')
            .select('*')
            .eq('user_id', teamMember.user_id)
            .in('requirement_id', requirementIds)
            .order('updated_date', { ascending: false });

          if (error) throw error;

          setCertificateHistory(data || []);
        } else {
          setCertificateHistory([]);
        }
      }
    } catch (error) {
      console.error('Error loading certificate history:', error);
      toast.error('Failed to load certificate history');
      setCertificateHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <Minus className="h-3 w-3 text-gray-400" />;
    }
    return sortDirection === "asc"
      ? <ChevronUp className="h-3 w-3 text-gray-600" />
      : <ChevronDown className="h-3 w-3 text-gray-600" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusTooltip = (qualification) => {
    const status = qualification.status;
    const daysUntilExpiry = getDaysUntilExpiry(qualification.expiry);

    switch (status) {
      case 'completed':
      case 'compliant':
        return (
          <div className="space-y-1">
            <div className="font-semibold">Valid Qualification</div>
            {qualification.issued && (
              <div className="text-xs">Uploaded: {formatDate(qualification.issued)}</div>
            )}
            {qualification.expiry && (
              <div className="text-xs">Expires: {formatDate(qualification.expiry)}</div>
            )}
            {daysUntilExpiry !== null && daysUntilExpiry > 30 && (
              <div className="text-xs text-emerald-200">{daysUntilExpiry} days remaining</div>
            )}
          </div>
        );

      case 'pending':
      case 'awaiting_certification':
        return (
          <div className="space-y-1">
            <div className="font-semibold">Qualification Required</div>
            <div className="text-xs">No certificate has been uploaded yet</div>
            <div className="text-xs">Action needed: Upload certificate or book training</div>
          </div>
        );

      case 'in_progress':
      case 'booked':
        return (
          <div className="space-y-1">
            <div className="font-semibold">Training Booked</div>
            <div className="text-xs">A training session has been scheduled</div>
            {qualification.issued && (
              <div className="text-xs">Booked on: {formatDate(qualification.issued)}</div>
            )}
          </div>
        );

      case 'quoting':
        return (
          <div className="space-y-1">
            <div className="font-semibold">Quote in Progress</div>
            <div className="text-xs">A training quote is being prepared</div>
            <div className="text-xs">You'll be notified when available</div>
          </div>
        );

      case 'expiring_soon':
        return (
          <div className="space-y-1">
            <div className="font-semibold">Expiring Soon</div>
            {qualification.expiry && (
              <div className="text-xs">Expires: {formatDate(qualification.expiry)}</div>
            )}
            {daysUntilExpiry !== null && (
              <div className="text-xs text-yellow-200 font-semibold">
                {daysUntilExpiry} {daysUntilExpiry === 1 ? 'day' : 'days'} remaining
              </div>
            )}
            <div className="text-xs">Action needed: Renew before expiry</div>
          </div>
        );

      case 'expired':
        return (
          <div className="space-y-1">
            <div className="font-semibold">Expired Qualification</div>
            {qualification.expiry && (
              <div className="text-xs">Expired: {formatDate(qualification.expiry)}</div>
            )}
            {daysUntilExpiry !== null && (
              <div className="text-xs text-red-200 font-semibold">
                {Math.abs(daysUntilExpiry)} {Math.abs(daysUntilExpiry) === 1 ? 'day' : 'days'} overdue
              </div>
            )}
            <div className="text-xs">Urgent action needed: Renew immediately</div>
          </div>
        );

      default:
        return <div className="font-semibold">Status Information</div>;
    }
  };

  if (transformedQualifications.length === 0) {
    return (
      <div className="w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="flex flex-col items-center justify-center text-gray-500">
          <FileText className="h-12 w-12 mb-3 opacity-50" />
          <p className="text-lg font-medium">No Qualifications Yet</p>
          <p className="text-sm mt-1">Qualifications will appear here once assigned in project training matrices</p>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="w-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="py-2.5 px-3.5 border-b bg-gray-50/50 flex items-center justify-between gap-4 h-11">
          <h3 className="text-sm font-semibold text-gray-900">My Qualifications</h3>

          {/* Search Bar */}
          <div className="flex items-center gap-3">
            <div className="relative w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search qualifications, categories, or spaces..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-7 text-xs"
              />
            </div>
            {searchQuery && (
              <div className="text-xs text-gray-600 whitespace-nowrap">
                {sortedQualifications.length} {sortedQualifications.length === 1 ? 'result' : 'results'}
              </div>
            )}
          </div>
        </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th
                className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort("name")}
              >
                <div className="flex items-center gap-1">
                  Qualification
                  <SortIcon field="name" />
                </div>
              </th>
              <th
                className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort("site")}
              >
                <div className="flex items-center gap-1">
                  Site/Project
                  <SortIcon field="site" />
                </div>
              </th>
              <th
                className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort("status")}
              >
                <div className="flex items-center gap-1">
                  Status
                  <SortIcon field="status" />
                </div>
              </th>
              <th
                className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort("expiry")}
              >
                <div className="flex items-center gap-1">
                  Expiry Date
                  <SortIcon field="expiry" />
                </div>
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Certificate
              </th>
              <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedQualifications.map((qualification, index) => {
              const statusInfo = statusConfig[qualification.status] || { label: qualification.status, color: 'gray' };
              const daysUntilExpiry = getDaysUntilExpiry(qualification.expiry);
              const categoryColors = {
                'Safety': 'bg-red-500',
                'Health & Safety': 'bg-red-500',
                'Equipment': 'bg-blue-500',
                'Environmental': 'bg-green-500',
                'Quality': 'bg-purple-500',
                'Training': 'bg-yellow-500',
                'Certification': 'bg-indigo-500',
                'Compliance': 'bg-pink-500'
              };
              const bgColor = categoryColors[qualification.category] || 'bg-gray-500';

              return (
                <tr
                  key={qualification.id}
                  className={index % 2 === 1 ? "bg-gray-50/50" : "bg-white"}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {qualification.name}
                        {/* Show project name inline if there are duplicates */}
                        {sortedQualifications.filter(q => q.name === qualification.name).length > 1 && (
                          <span className="text-xs text-gray-500 font-normal ml-1">
                            ({qualification.site?.name})
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">{qualification.category}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {qualification.sites && qualification.sites.length > 0 ? (
                      <div className="flex items-center gap-2">
                        {/* Show first space as chip */}
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-50 text-gray-700 text-xs font-medium rounded-md border border-gray-200 max-w-[200px]">
                          <Building className="h-3 w-3 text-gray-500 flex-shrink-0" />
                          <span className="truncate">{qualification.sites[0].name}</span>
                        </span>
                        {/* Show "+x" chip with popover if more than 1 space */}
                        {qualification.sites.length > 1 && (
                          <Popover open={hoveredQualificationId === qualification.id} onOpenChange={(open) => !open && setHoveredQualificationId(null)}>
                            <PopoverTrigger asChild>
                              <button
                                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-md border border-blue-200 hover:bg-blue-100 transition-colors cursor-pointer"
                                onMouseEnter={() => setHoveredQualificationId(qualification.id)}
                                onMouseLeave={() => setHoveredQualificationId(null)}
                              >
                                +{qualification.sites.length - 1}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-80 p-3"
                              align="start"
                              onMouseEnter={() => setHoveredQualificationId(qualification.id)}
                              onMouseLeave={() => setHoveredQualificationId(null)}
                            >
                              <div className="space-y-2">
                                <h4 className="font-semibold text-sm text-gray-900 mb-2">All Spaces</h4>
                                {qualification.sites.map((site, siteIdx) => (
                                  <div key={siteIdx} className="flex items-center gap-2 p-2 rounded-md bg-gray-50 border border-gray-200">
                                    <Building className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium text-gray-900">{site.name}</div>
                                      {site.location && (
                                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                                          <MapPin className="h-3 w-3" />
                                          {site.location}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-blue-400" />
                        <span className="text-sm text-gray-600">Global Qualification</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-md border ${statusInfo.color} cursor-help`}>
                            {statusInfo.icon && <statusInfo.icon className={`h-3 w-3 ${statusInfo.iconColor}`} />}
                            {statusInfo.label}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          {getStatusTooltip(qualification)}
                        </TooltipContent>
                      </Tooltip>

                      {/* Secondary statuses chip with popover */}
                      {qualification.secondaryStatuses && qualification.secondaryStatuses.length > 0 && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-md border border-gray-300 bg-gray-50 text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors">
                              +{qualification.secondaryStatuses.length}
                            </span>
                          </PopoverTrigger>
                          <PopoverContent side="top" className="w-auto p-3">
                            <div className="space-y-2">
                              <p className="text-xs font-semibold text-gray-700 mb-2">Also:</p>
                              {qualification.secondaryStatuses.map((secondary, idx) => {
                                const secondaryInfo = statusConfig[secondary.status] || { label: secondary.status, color: 'gray' };
                                return (
                                  <div key={idx} className="flex items-center gap-2">
                                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-md border ${secondaryInfo.color}`}>
                                      {secondaryInfo.icon && <secondaryInfo.icon className={`h-3 w-3 ${secondaryInfo.iconColor}`} />}
                                      {secondaryInfo.label}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{formatDate(qualification.expiry)}</div>
                      {daysUntilExpiry !== null && (
                        <div className="text-xs text-gray-500">
                          {daysUntilExpiry < 0
                            ? `Expired ${Math.abs(daysUntilExpiry)} days ago`
                            : daysUntilExpiry === 0
                            ? 'Expires today'
                            : daysUntilExpiry <= 30
                            ? `${daysUntilExpiry} days remaining`
                            : `Valid`
                          }
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {/* View button - show if certificate exists */}
                      {qualification.certificateUrl && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewCertificate(qualification.certificateUrl)}
                          className="h-8 text-xs"
                        >
                          <Eye className="h-3 w-3 mr-1.5" />
                          View
                        </Button>
                      )}

                      {/* Upload button - show for missing/expired/quoting (even with certificate for expired) */}
                      {((!qualification.certificateUrl && (qualification.status === 'pending' || qualification.status === 'quoting')) || qualification.status === 'expired') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleFileUpload(qualification.id)}
                          className="h-8 text-xs"
                        >
                          <Upload className="h-3 w-3 mr-1.5" />
                          Upload
                        </Button>
                      )}

                      {/* Quote button - show for missing, expiring, expired, or valid (for early renewals) */}
                      {(qualification.status === 'pending' || qualification.status === 'expiring_soon' || qualification.status === 'expired' || qualification.status === 'completed' || qualification.status === 'compliant') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRequestQuote(qualification)}
                          className="h-8 text-xs"
                        >
                          Quote
                        </Button>
                      )}
                    </div>
                  </td>

                  {/* Actions column with three-dot menu */}
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleViewCertificateHistory(qualification)}
                        >
                          <History className="h-4 w-4 mr-2" />
                          See All Certificates
                        </DropdownMenuItem>
                        {qualification.certificateUrl && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleEditCertificate(qualification)}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Certificate
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteCertificateClick(qualification)}
                              className="text-orange-600 focus:text-orange-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Certificate
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleRemoveAssignmentClick(qualification)}
                          className="text-red-600 focus:text-red-600"
                        >
                          <UserX className="h-4 w-4 mr-2" />
                          Remove Assignment
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>

    <QuoteModalV2
      isOpen={quoteModalOpen}
      onClose={() => {
        setQuoteModalOpen(false);
        setSelectedCell(null);
      }}
      selectedCell={selectedCell}
      onQuote={async (data) => {
        // Refresh the qualifications data after quote is created
        if (data?.success) {
          toast.success('Quote request submitted successfully');
          setQuoteModalOpen(false);
          setSelectedCell(null);

          // Trigger refresh to update status
          if (onUploadSuccess) {
            onUploadSuccess();
          }
        }
      }}
    />

    {/* Edit Certificate Dialog */}
    <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Certificate Details</DialogTitle>
          <DialogDescription>
            Update the expiry date and certificate number for this qualification.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="expiryDate">Expiry Date</Label>
            <Input
              id="expiryDate"
              type="date"
              value={editFormData.expiryDate}
              onChange={(e) => setEditFormData({ ...editFormData, expiryDate: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="certificateNumber">Certificate Number</Label>
            <Input
              id="certificateNumber"
              type="text"
              placeholder="Enter certificate number"
              value={editFormData.certificateNumber}
              onChange={(e) => setEditFormData({ ...editFormData, certificateNumber: e.target.value })}
            />
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={() => setEditModalOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveEdit}>
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Delete Certificate Confirmation Dialog */}
    <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-600">
            <AlertCircle className="h-5 w-5" />
            Delete Certificate?
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this certificate? The qualification assignment will remain, but the status will be reset to pending.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-3 justify-end pt-4">
          <Button variant="outline" onClick={() => {
            setDeleteConfirmOpen(false);
            setActionQualification(null);
          }}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            className="bg-orange-600 hover:bg-orange-700"
            onClick={handleDeleteCertificate}
          >
            Delete Certificate
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Remove Assignment Confirmation Dialog */}
    <Dialog open={removeConfirmOpen} onOpenChange={setRemoveConfirmOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-5 w-5" />
            Remove Assignment?
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to remove this qualification assignment? This will permanently delete both the assignment and any uploaded certificate. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-3 justify-end pt-4">
          <Button variant="outline" onClick={() => {
            setRemoveConfirmOpen(false);
            setActionQualification(null);
          }}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleRemoveAssignment}
          >
            Remove Assignment
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Certificate History Dialog */}
    <Dialog open={certificateHistoryOpen} onOpenChange={setCertificateHistoryOpen}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Certificate History - {actionQualification?.name}
          </DialogTitle>
          <DialogDescription>
            All certificates uploaded for this qualification
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {loadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500">Loading history...</div>
            </div>
          ) : certificateHistory.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mb-3 opacity-50" />
              <p>No certificate history found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {certificateHistory.map((record, index) => (
                <div
                  key={record.id}
                  className={`p-4 rounded-lg border ${
                    index === 0 ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {index === 0 && (
                          <Badge className="bg-blue-600">Current</Badge>
                        )}
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-md border ${
                          statusConfig[record.status]?.color || 'bg-gray-100 text-gray-700 border-gray-200'
                        }`}>
                          {statusConfig[record.status]?.label || record.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-500">Uploaded:</span>{' '}
                          <span className="font-medium">
                            {record.completed_date ? formatDate(record.completed_date) : 'Not set'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Expiry:</span>{' '}
                          <span className="font-medium">
                            {record.expiry_date ? formatDate(record.expiry_date) : 'Not set'}
                          </span>
                        </div>
                        {record.certificate_number && (
                          <div className="col-span-2">
                            <span className="text-gray-500">Certificate #:</span>{' '}
                            <span className="font-medium">{record.certificate_number}</span>
                          </div>
                        )}
                        <div className="col-span-2">
                          <span className="text-gray-500">Created:</span>{' '}
                          <span className="font-medium">{formatDate(record.created_date)}</span>
                        </div>
                      </div>
                    </div>
                    {record.evidence_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewCertificate(record.evidence_url)}
                        className="ml-4"
                      >
                        <Eye className="h-3 w-3 mr-1.5" />
                        View
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={() => setCertificateHistoryOpen(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </TooltipProvider>
  );
}